import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import UnstableConnectionIcon from '../components/shared/icon/UnstableConnectionIcon';
import { useBanner } from '../contexts/BannerContext';
import InfoService from '../services/InfoSevice';

const slowMs = 3 * 1000; // 3 s
const problematicMs = 10 * 1000; // 10 seconds
const checkInterval = 2 * 60 * 1000; // 2 minutes

export const useDetectProblematicConnection = () => {
  const banners = useBanner();
  const { t } = useTranslation('common');
  const warningBannerIdRef = useRef<string | null>(null);
  const bannerTypeRef = useRef<'unstable' | 'slow' | null>(null);

  const warnSlowConnection = useCallback(
    (isTimeout: boolean) => {
      const newBannerType = isTimeout ? 'unstable' : 'slow';
      if (bannerTypeRef.current === newBannerType) return;

      banners.removeBanner(warningBannerIdRef.current!);
      const id = banners.showBanner({
        icon: <UnstableConnectionIcon className="h-5 w-5" />,
        message: isTimeout ? t('bad-connection.unstable') : t('bad-connection.poor'),
      });

      warningBannerIdRef.current = id;
      bannerTypeRef.current = newBannerType;
    },
    [banners, t],
  );

  useEffect(() => {
    const checkConnectionSpeed = () => {
      const cancelationToken = new AbortController();
      const timeout = setTimeout(() => {
        cancelationToken.abort();
      }, problematicMs);

      InfoService.ping(new Date().toISOString(), cancelationToken.signal)
        .then((res) => {
          clearTimeout(timeout);
          const duration = Date.now() - new Date(res.data).getTime();
          if (duration > slowMs) {
            warnSlowConnection(false);
          } else {
            if (warningBannerIdRef.current) {
              banners.removeBanner(warningBannerIdRef.current);
              warningBannerIdRef.current = null;
              bannerTypeRef.current = null;
            }
          }
        })
        .catch(() => {
          // Error, or more likely, request cancelled
          warnSlowConnection(true);
        });
    };

    checkConnectionSpeed();
    const interval = setInterval(checkConnectionSpeed, checkInterval);
    return () => clearInterval(interval);
  }, [banners, warnSlowConnection]);
};
